import { z } from "zod";

/**
 * first-home-scheme — government first-home-buyer scheme eligibility MAP.
 *
 * From `state × income × deposit × build-type × price`, return which of the four
 * schemes the buyer appears eligible for and how they stack:
 *
 *   1. Australian Government 5% Deposit Scheme (federal) — renamed from the Home
 *      Guarantee Scheme on 1 Oct 2025; income caps REMOVED, no place limits, no
 *      LMI, higher price caps (e.g. Sydney $1.5m). Date-keyed.
 *   2. FHSS — First Home Super Saver scheme (federal, super). Information only;
 *      cross-link, never advise a release.
 *   3. State FHOG — First Home Owner Grant ($10k NSW / $10k VIC / $30k QLD,
 *      QLD reverts to $15k for contracts after 30 Jun 2026). Mostly new-build only.
 *   4. State stamp-duty exemption/concession (full nil ≤$800k NSW / ≤$600k VIC /
 *      ≤$700k QLD established; QLD new-builds nil regardless of value from
 *      1 May 2025).
 *
 * HARD RULE (binding — the regulatory C2 kill-line): the schemes themselves are
 * government programs (not `s764A` financial products / not credit), BUT the
 * downstream MORTGAGE is NCCP-regulated credit and *credit assistance* is a
 * licensed activity. NO broker / lender / loan-product handoff may reach any
 * surface that displays a PERSONALISED eligibility result. This is encoded as an
 * architectural guard (`assertNoCreditHandoff` + the `surface` allow-list) and a
 * test, NOT doc prose. See `calculate.ts`.
 *
 * Pure: `inputs × rules × assessmentDate -> outputs`. No I/O, no Date.now() —
 * the assessment date is always a parameter, which is what makes the QLD FHOG
 * 30-Jun-2026 sunset resolvable historically.
 *
 * v1 scope: NSW / VIC / QLD (the CITED jurisdictions in the figure ledger).
 * WA/SA/TAS/ACT/NT are intentionally NOT shipped — they have no verified table
 * yet, and a wrong threshold is an ACL s18 misleading-conduct exposure.
 */

/** The three jurisdictions shipped in v1 (CITED in the figure ledger). */
export const FHB_STATES = ["NSW", "VIC", "QLD"] as const;
export type FhbState = (typeof FHB_STATES)[number];

/** Property build type — gates FHOG (mostly new-only) and the QLD duty branch. */
export const FHB_BUILD_TYPES = ["new", "established"] as const;
export type FhbBuildType = (typeof FHB_BUILD_TYPES)[number];

/** The four schemes this map resolves eligibility for. */
export const FHB_SCHEMES = [
  "five_percent_deposit", // 1. Australian Government 5% Deposit Scheme (federal)
  "fhss", // 2. First Home Super Saver (federal)
  "fhog", // 3. State First Home Owner Grant
  "stamp_duty_relief", // 4. State stamp-duty exemption/concession
] as const;
export type FhbScheme = (typeof FHB_SCHEMES)[number];

export const FhbInputSchema = z.object({
  /** State/territory the property is in. v1 = NSW / VIC / QLD only. */
  state: z.enum(FHB_STATES),
  /** Purchase price of the property, in dollars. */
  price: z.number().min(0),
  /**
   * Buyer's (or combined, if a couple) annual income, in dollars. Retained for
   * the result surface even though the 5% scheme dropped its income cap on
   * 1 Oct 2025 — historical assessments before that date still test it.
   */
  income: z.number().min(0),
  /**
   * Deposit as a FRACTION of price (0.05 = 5%). The 5% scheme needs ≥5% (or 2%
   * for the single-parent Family Home Guarantee stream).
   */
  depositFraction: z.number().min(0).max(1),
  /** Whether the home is a new build or an established dwelling. */
  buildType: z.enum(FHB_BUILD_TYPES),
  /**
   * Single parent or legal guardian buying alone — unlocks the 2% Family Home
   * Guarantee deposit floor on the 5% scheme. Defaults to false.
   */
  singleParentOrGuardian: z.boolean().default(false),
  /** Genuine first home buyer (no prior ownership). Defaults to true. */
  firstHomeBuyer: z.boolean().default(true),
});

export type FhbInput = z.infer<typeof FhbInputSchema>;

/** Per-scheme eligibility verdict on the entered figures. */
export interface SchemeVerdict {
  scheme: FhbScheme;
  /** True if the buyer appears eligible on these figures. */
  eligible: boolean;
  /** The gates that decided it — each a short, sourced, plain reason. */
  reasons: string[];
  /**
   * The scheme's headline value as a GATED MAXIMUM, framed never as "you'll
   * get $X". For FHOG this is the grant amount; for duty relief it's
   * "full exemption" / "concession" / null; for the federal schemes it's the
   * non-dollar benefit. Null where the scheme grants no dollar figure.
   */
  maxBenefit: SchemeBenefit | null;
}

/** A scheme's benefit, expressed as a gated maximum (not a personalised $). */
export type SchemeBenefit =
  | { kind: "grant"; amount: number }
  | { kind: "duty_exemption"; level: "full" | "concession" }
  | { kind: "lmi_waiver" } // 5% scheme: avoids LMI, no fixed $
  | { kind: "super_release" }; // FHSS: information-only

export interface FhbOutput {
  /** The assessment date these verdicts were resolved against (ISO). */
  assessmentDate: string;
  /** The jurisdiction's rule period that was in force on that date. */
  periodLabel: string;
  /** One verdict per scheme, in `FHB_SCHEMES` order. */
  verdicts: SchemeVerdict[];
  /** The schemes that came back eligible — the "stack". */
  eligibleSchemes: FhbScheme[];
  /**
   * How the eligible schemes interact — the cross-scheme gotchas that no single
   * government checker surfaces (e.g. 5%-scheme price cap exceeds the duty
   * exemption ceiling; new-vs-established FHOG = $0).
   */
  stackingNotes: string[];
}

// ───────────────────────── Rule-period table types ─────────────────────────

/** Provenance for one jurisdiction's table within a rule period. */
export interface FhbSource {
  /** The publishing revenue office / agency. */
  authority: string;
  /** Primary-source URL. */
  url: string;
}

/** Federal 5% Deposit Scheme parameters for a rule period. */
export interface FivePercentScheme {
  /** Marketed name in force this period. */
  name: string;
  /** Minimum deposit fraction for standard first home buyers. */
  minDeposit: number;
  /** Minimum deposit fraction for single parents / guardians (FHG stream). */
  singleParentMinDeposit: number;
  /** True once income caps were removed (1 Oct 2025). */
  incomeCapsRemoved: boolean;
  /**
   * Income caps that applied BEFORE removal (single / couple), or null once
   * removed. Kept so historical periods test the old gate.
   */
  incomeCapSingle: number | null;
  incomeCapCouple: number | null;
  /** Price cap by capital-city / region group, in dollars. */
  priceCaps: Record<string, number>;
  source: FhbSource;
}

/** State First Home Owner Grant rules for a rule period. */
export interface FhogRules {
  /** Grant amount in dollars (the period's figure — QLD swaps at the sunset). */
  amount: number;
  /** FHOG is new-build only in NSW/VIC/QLD; established = $0. */
  newBuildOnly: boolean;
  /** Upper property-value cap for the grant, in dollars (null = uncapped). */
  valueCap: number | null;
  source: FhbSource;
}

/** State stamp-duty first-home relief for a rule period. */
export interface DutyRules {
  /** Full-exemption ceiling: nil duty at/below this value, in dollars. */
  fullExemptionCeiling: number;
  /** Tapered-concession ceiling: concession between exemption + this, dollars. */
  concessionCeiling: number;
  /**
   * QLD-only: new builds are nil duty regardless of value from 1 May 2025. When
   * true, a NEW build of any price gets full exemption (overrides the ceilings).
   */
  newBuildExemptRegardlessOfValue: boolean;
  source: FhbSource;
}

/** First Home Super Saver (federal) parameters for a rule period. */
export interface FhssRules {
  /** Max eligible contributions releasable, in dollars. */
  maxReleasable: number;
  source: FhbSource;
}

/**
 * A complete first-home-scheme ruleset for ONE rule period, date-keyed. A period
 * is bounded by `[effectiveFrom, effectiveTo]` (ISO; `effectiveTo` null = open).
 * Per-state tables are nested so each jurisdiction's reprice schedule is visible
 * and individually sourced. The QLD FHOG 30-Jun-2026 sunset is modelled as two
 * registered periods, never a mutation of one.
 */
export interface FhbRuleSet {
  /** Stable registry key, e.g. "2025-10-01". */
  periodKey: string;
  /** Human label, surfaced in copy/audit. */
  periodLabel: string;
  /** Inclusive ISO start of this period. */
  effectiveFrom: string;
  /** Inclusive ISO end of this period, or null for the open current period. */
  effectiveTo: string | null;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;
  /** Federal 5% Deposit Scheme (formerly Home Guarantee Scheme). */
  fivePercent: FivePercentScheme;
  /** Federal First Home Super Saver scheme. */
  fhss: FhssRules;
  /** Per-state FHOG tables. */
  fhog: Record<FhbState, FhogRules>;
  /** Per-state stamp-duty relief tables. */
  duty: Record<FhbState, DutyRules>;
}
